import axios from 'axios'
import { store } from 'config/redux'
import axiosRetry from 'axios-retry'
import { v4 as uuidv4 } from 'uuid'
import { getAppVersion } from 'utils/appConfiguration'
import { language as contextLanguage } from 'context/LanguageContext/strings'
import jwtDecode from 'jwt-decode'
import { getHashFromDOMScriptTag, setItem, isItemExist, getItem } from 'utils/storageUtils'
import lambdaUrlMapping from 'utils/lambdaUrlMapping'
import { getCurrentCountry, isEmpty } from '../../utils/appUtils'
import { logsUrl } from '../../components/ErrorBoundary/url'
import { environment } from '../../config/environment'
import { getBaseURL, getServiceKey, get7RBaseURL } from './networkSettings'

// import { setJWT } from 'modules/appSettings/actions'
// import { signOut } from 'modules/authentication/actions'
// const UNAUTHORIZED_STATUSES = [401, 400]

const METHODS_WITH_BODY = ['POST', 'PATCH', 'DELETE','PUT']
// const authTokenForSalesForceUrl =
//     'https://mcy8q-n2f8ttyv729bcg5v83dsp4.auth.marketingcloudapis.com/v2/token'
// const sendAppLinkToMobileUrl =
//     'https://mcy8q-n2f8ttyv729bcg5v83dsp4.rest.marketingcloudapis.com/sms/v1/queueMO'

axiosRetry(axios, {
    retries: 0,
    retryDelay: count => count * 1000,
    retryCondition: error => {
        const shouldRetry =
            error.code !== 'ECONNABORTED' &&
            (!error.response || (error.response.status >= 500 && error.response.status <= 599))
        if (shouldRetry) {
            console.log('Retrying api', error)
        }
        return shouldRetry
    },
})

// Global error interceptor to transform AxiosErrors before they propagate
axios.interceptors.response.use(
    response => response,
    error => {
        // Transform the error to prevent raw AxiosError from being thrown
        const transformedError = error?.response || {
            status: 0,
            code: error?.code || 'UNKNOWN_ERROR',
            message: error?.message || 'An error occurred',
            isNetworkError: error?.code === 'ERR_NETWORK',
            isTimeout: error?.code === 'ECONNABORTED',
            isCanceled: error?.code === 'ERR_CANCELED',
            originalError: error
        }
        return Promise.reject(transformedError)
    }
)

function postLoggingData(data) {
    if (data && data.statusCode == 200 && data.source.indexOf('log') == -1) {
        api(logsUrl, 'POST', data).catch(() => {
            // Silently ignore logging failures to prevent unhandled rejections
        })
    }
}

function getLoggingData(error, url, statusCode, response, defaultURL) {
    const { appSettings } = store.getState()
    const isApp = appSettings.get('isApp')
    const appDetails = appSettings.get('appDetails')
    let sub
    if (!isEmpty(appSettings)) {
        const jwt = appSettings.get('jwt')
        if (!isEmpty(jwt)) {
            sub = jwtDecode(jwt).sub
        }
    }
    let message = `API Error:${JSON.stringify(error)} || API Url:${url}`
    if (!error) {
        message = `API Success || API Url:${url}`
    }
    // logs to server call below
    const data = {
        message,
        logLevel: 'stream',
        loggedBy: 'webAppNetwork',
        sub,
        appDetails,
        isApp,
        statusCode,
        source: defaultURL
            ? lambdaUrlMapping.get(defaultURL)
            : lambdaUrlMapping.get(url)
              ? lambdaUrlMapping.get(url)
              : url,
        status: statusCode == 200 ? 'success' : 'failed',
        baseURL: getBaseURL(),
        appName: '7Now Customer APP',
    }
    if (url == '/cart/v4/orders' || url == '/cart/v4/orders/pickup') {
        data.data = response
    }
    return data
}

export async function getIPAddress() {
    const hashCode = getHashFromDOMScriptTag()
    const key = 'ip'
    if (isItemExist(key)) {
        const ipObj = getItem(key, hashCode)
        return ipObj.item
    }

    const address = await axios({
        url: 'https://icanhazip.com',
    })
        .then(response => response?.data || '')
        .catch(error => {
            console.log(' Error is ', error)
            return ''
        })

    setItem(key, address.trim(), hashCode)
    return address
}

const makeRequest = (url, requestInfo, mApiFlag, cancelToken, isRewardsFlag) => {
    if (mApiFlag || isRewardsFlag) {
        delete requestInfo.headers['x-api-key']
    }
    let baseUrl
    if (isRewardsFlag) {
        baseUrl = get7RBaseURL()
    } else if (mApiFlag) {
        baseUrl = `${get7RBaseURL()}/auth`
    } else {
        baseUrl = getBaseURL()
    }

    return axios({
        method: requestInfo.method,
        url,
        timeout: requestInfo.timeout,
        headers: requestInfo.headers,
        data: requestInfo.body,
        'axios-retry': requestInfo['axios-retry'],
        baseURL: baseUrl,
        cancelToken,
    })
}

export const api = (
    url,
    method,
    request,
    retryCount = 0,
    timeOut = null,
    mApiFlag = false,
    language = null,
    cancelToken = null,
    isRewardsFlag = false,
    isEmailVerification = false,
    defaultURL
) => {
    // TO DO: Need to check for WEB what should be the header informatiom
    const requestInfo = getRequestInfo(
        method,
        request,
        retryCount,
        timeOut,
        mApiFlag,
        language,
        isRewardsFlag,
        isEmailVerification
    )

    return makeRequest(url, requestInfo, mApiFlag, cancelToken, isRewardsFlag)
        .then(response => {
            if (url.indexOf('log') == -1) {
                const _data = getLoggingData(null, url, 200, response.data, defaultURL)
                _data.method = method
                postLoggingData(_data)
            }
            return response.data
        })
        .catch(error => {
            if (url.indexOf('log') == -1) {
                const data = getLoggingData(error?.originalError || error, url, 501, defaultURL)
                data.method = method
                makeRequest(logsUrl, getRequestInfo('POST', data, 0, null, false), false).catch(() => {})
            }
            // Error is already transformed by the interceptor
            throw error
        })
}

const getRequestInfo = (
    method,
    request,
    retry,
    timeOut,
    mApiFlag,
    language,
    isRewardsFlag,
    isEmailVerification
) => {
    const { appSettings, configuration, location, user, authentication } = store.getState()
    const clientAccessToken = authentication.get('emailVerificationClientToken')
    const token =
        isEmailVerification && clientAccessToken ? clientAccessToken : appSettings.get('jwt')
    const isApp = appSettings.get('isApp')
    const appDetails = appSettings.get('appDetails')

    const timeout = timeOut != null ? timeOut : configuration.get('timeout') || 30
    const date = new Date().toTimeString().slice(12, 17)
    const convertedOffset = `${date.slice(0, 3)}:${date.slice(3, 6)}`
    const serviceKey = getServiceKey()
    const country = getCurrentCountry(location, user)
    const defaultHeaders = {
        'x-api-key': serviceKey,
        'Content-Type': 'application/json',
        'Accept-Encoding': 'gzip',
        'Accept-Language': contextLanguage,
    }

    if (mApiFlag || isRewardsFlag) {
        defaultHeaders['User-Agent'] = 'SevenEleven7NOW'
        defaultHeaders['X-SEI-PLATFORM'] = '7now_web'
        defaultHeaders['X-SEI-VERSION'] = getAppVersion()
        defaultHeaders['X-SEI-TZ'] = convertedOffset
        defaultHeaders['X-SEI-DEVICE-UUID'] = appSettings.get('clientID') || uuidv4()
    }

    if (language) {
        defaultHeaders['Accept-Language'] = language
    }

    if (environment !== 'development' && !(mApiFlag || isRewardsFlag)) {
        // defaultHeaders['x-711-locale'] = `country=${country};currency=${
        //     country === countryCode.CA ? 'CAD' : 'USD'
        // };lang=en-US`
        if (isApp) {
            const {
                source: app_name,
                os: platform,
                appVersion: app_version,
                osVersion: os_version,
            } = appDetails
            // defaultHeaders[
            //     'x-device-info'
            // ] = `app_name=${app_name};platform=${platform};os_version=${os_version};app_version=${app_version};country=${country}`
        } else {
            // defaultHeaders['x-device-info'] = `app_name=7Now_Web;platform=web;country=${country}`
        }
    }

    if (token && !mApiFlag) {
        defaultHeaders.Authorization = `Bearer ${token}`
    }

    const requestInfo = {
        method,
        headers: defaultHeaders,
        timeout: timeout * 1000,
        'axios-retry': {
            retries: retry,
        },
    }

    if (METHODS_WITH_BODY.includes(method) && request) {
        requestInfo.body = JSON.stringify(request)
    }
    return requestInfo
}

/** TOKEN REFRESH LOGIC * */

// let subscribers = []
// let isAlreadyFetchingAccessToken = false

// async function onAccessTokenFetched(access_token) {
//     if (process.env.REACT_APP_ENV === 'qa') {
//         console.log('Calling subscribers list after token refresh', subscribers)
//     }
//     subscribers = subscribers.map(request => {
//         request.headers.Authorization = `Bearer ${access_token}`
//         return axios(request)
//     })
//     subscribers = []
//     isAlreadyFetchingAccessToken = false
// }

// function addSubscriber(callback) {
//     subscribers.push(callback)
// }

// axios.interceptors.response.use(
//     function (response) {
//         if (isRefreshAPI(response)) {
//             if (response.data && response.data.token) {
//                 store.dispatch(setJWT(response.data.token))
//                 onAccessTokenFetched(response.data.token)
//             }
//         }
//         return response
//     },
//     function (error) {
//         const {
//             config,
//             response: { status },
//         } = error
//         const originalRequest = config

//         if (UNAUTHORIZED_STATUSES.includes(status)) {
//             if (isRefreshAPI(error)) {
//                 //Logout the user and clear the subscriptions
//                 isAlreadyFetchingAccessToken = false
//                 store.dispatch(signOut())
//                 subscribers = []
//                 return Promise.reject(error)
//             }
//             addSubscriber(originalRequest)
//             console.log(' isAlreadyFetchingAccessToken ', isAlreadyFetchingAccessToken)

//             if (!isAlreadyFetchingAccessToken) {
//                 isAlreadyFetchingAccessToken = true

//                 if (process.env.REACT_APP_ENV === 'qa') {
//                     console.log(' Inside Refresh API flow')
//                 }
//                 //Call refresh Token
//                 const { appSettings } = store.getState()
//                 const token = appSettings.get('jwt')
//                 const requestInfo = getRequestInfo('POST', { token: token })

//                 return makeRequest(refreshTokenURL, requestInfo)
//             }
//         }
//         return Promise.reject(error)
//     }
// )

// const isRefreshAPI = originalRequest => {
//     return originalRequest &&
//         originalRequest.config &&
//         originalRequest.config.url.indexOf('/auth/refreshToken') !== -1
//         ? true
//         : false
// }
